import React, { useEffect, useState } from "react";

import { CTooltip } from "@coreui/react";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Button, Col, Row } from "reactstrap";

import ActivityViewModal from "./ActivityViewModal";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import { ContentCard } from "../../ContentCard";
import Loading from "../../Loading";

/* Atividades sugeridas, agrupadas por habilidade.

   Uma atividade pode ter mais de uma habilidade, e cada uma tem as suas
   sugestões — activity/activity/listByAbility responde por uma habilidade só, e
   é justamente esse recorte que a seção mostra. Sem page/pageSize a rota devolve
   a lista inteira, então o "N atividade(s) encontrada(s)" do cabeçalho é sempre
   o número de cards logo abaixo dele.

   A descrição da habilidade não vem no singleView da atividade (lá só há
   `code`), então cada grupo busca a sua em classification/ability/singleView.

   Só entram atividades públicas (é o recorte da própria rota) e a atividade do
   relatório é descartada — ela tem essa habilidade e se sugeriria. */
const fetchAbilityGroups = async (abilities, activityId, auth) => {
  if (!abilities.length) return [];

  return Promise.all(
    abilities.map(async ability => {
      const [respActivities, respAbility] = await Promise.all([
        axios.post(
          `${URL.api}activity/activity/listByAbility`,
          { ability_id: ability.ability_id },
          auth
        ),
        /* A descrição é um detalhe do cabeçalho: se a rota falhar o grupo
           continua, mostrando só o código que já veio da atividade. */
        axios
          .get(
            `${URL.api}classification/ability/singleView/${ability.ability_id}`,
            auth
          )
          .catch(() => null)
      ]);

      const activities = new Map();

      (respActivities.data.data ?? []).forEach(activity => {
        if (activity.id === Number(activityId)) return;
        if (activities.has(activity.id)) return;

        activities.set(activity.id, activity);
      });

      const detail = respAbility?.data?.data?.ability;

      return {
        abilityId: ability.ability_id,
        code: ability.code || detail?.code,
        description: detail?.description,
        activities: [...activities.values()]
      };
    })
  );
};

/* Turmas em que a atividade do relatório está alocada, cada uma com o recorte
   que o link da alocação precisa levar (grupo/regional/escola vêm da turma, não
   do papel de quem acessa) e com os estudantes que a alocação alcança — é por
   eles que a seleção da tabela é separada por turma. */
const fetchAllocatedRooms = async (allocations, auth) => {
  const roomIds = [
    ...new Set(allocations.map(allocation => allocation.room_id))
  ];

  if (!roomIds.length) return [];

  const responses = await Promise.all(
    roomIds.map(roomId =>
      axios.get(`${URL.api}community/room/singleView/${roomId}`, {
        ...auth,
        params: { users: false }
      })
    )
  );

  return roomIds.map((roomId, index) => {
    const room = responses[index].data.data?.room;
    const userIds = allocations
      .filter(allocation => allocation.room_id === roomId)
      .flatMap(allocation => allocation.users ?? [])
      .map(user => user.user_id);

    return {
      roomId,
      roomName: room?.name,
      groupId: room?.group_id,
      regionId: room?.region_id,
      schoolId: room?.school_id,
      userIds: [...new Set(userIds)]
    };
  });
};

// Seção do relatório da atividade (modal "Estatísticas da atividade", em
// /activity/list) que sugere atividades e leva a seleção para a alocação.
//
// `selectedUserIds`/`studentIds` vêm de fora porque quem marca os estudantes é a
// tabela do Resumo da Atividade, que é irmã desta seção — aqui eles só decidem
// o que o "Disponibilizar" manda para a tela de alocação.
export default function AtividadesSugeridas({
  activityId,
  selectedUserIds = [],
  studentIds = []
}) {
  const token = useSelector(state => state.user.token);
  const activeRoles = useSelector(state => state.general.active_roles);

  const [loading, setLoading] = useState(true);
  const [abilities, setAbilities] = useState([]);
  const [abilityGroups, setAbilityGroups] = useState([]);
  const [rooms, setRooms] = useState([]);
  /* Uma atividade por vez: a alocação é uma só, e escolher várias abriria uma
     aba por atividade sem o professor pedir. Guarda o id, não uma lista. */
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [viewedActivity, setViewedActivity] = useState(null);

  useEffect(() => {
    if (!activityId) return;

    let current = true;
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    setLoading(true);
    setSelectedActivityId(null);

    (async () => {
      try {
        const [respActivity, respAllocations] = await Promise.all([
          axios.get(
            `${URL.api}activity/activity/singleView/${activityId}`,
            auth
          ),
          /* room_id/user_id em 0 é "sem recorte": traz todas as alocações da
             atividade, que é de onde saem as turmas e os estudantes delas. */
          axios.post(
            `${URL.api}activity/allocate/list`,
            { activity_id: activityId, room_id: 0, user_id: 0 },
            auth
          )
        ]);

        const activityAbilities = respActivity.data.data?.abilities ?? [];
        /* A rota responde `success: false` quando a atividade nunca foi
           alocada, e aí `data` não vem — sem turma, mas a seção continua
           sugerindo atividades. */
        const allocations = respAllocations.data.data ?? [];

        const [groups, allocatedRooms] = await Promise.all([
          fetchAbilityGroups(activityAbilities, activityId, auth),
          fetchAllocatedRooms(allocations, auth)
        ]);

        if (!current) return;

        setAbilities(activityAbilities);
        setAbilityGroups(groups);
        setRooms(allocatedRooms);
      } catch {
        if (!current) return;

        setAbilities([]);
        setAbilityGroups([]);
        setRooms([]);
        toastr.error(
          "Falha",
          "Não foi possível carregar as atividades sugeridas."
        );
      } finally {
        if (current) setLoading(false);
      }
    })();

    return () => {
      current = false;
    };
  }, [activityId]);

  /* Clicar na atividade já marcada desmarca: é o único jeito de voltar a "nada
     escolhido" sem um botão só para isso. */
  const toggleActivity = id => {
    setSelectedActivityId(current => (current === id ? null : id));
  };

  /* Nenhum estudante marcado é a turma toda — e todos marcados também: a tela
     de alocação já trata a ausência de `user_ids` como "a turma inteira", e
     mandar a lista nominal do que é a turma só deixaria a alocação presa aos
     estudantes de hoje. */
  const wholeClass =
    !selectedUserIds.length ||
    (studentIds.length > 0 && selectedUserIds.length >= studentIds.length);

  /* Para onde o "Disponibilizar" aponta: uma alocação por turma.

     Com estudantes marcados, cada turma leva só os seus, e a que ficou sem
     nenhum sai da lista. Se a atividade do relatório não tem alocação nenhuma
     não há turma para levar, mas o recorte do papel de quem acessa ainda abre os
     filtros da alocação no lugar certo. */
  const allocationTargets = () => {
    if (!rooms.length) {
      return [
        {
          roomId: null,
          groupId: activeRoles.group_id,
          regionId: activeRoles.region_id,
          schoolId: activeRoles.school_id,
          userIds: []
        }
      ];
    }

    if (wholeClass) {
      return rooms.map(room => ({ ...room, userIds: [] }));
    }

    const targets = rooms
      .map(room => ({
        ...room,
        userIds: room.userIds.filter(userId => selectedUserIds.includes(userId))
      }))
      .filter(room => room.userIds.length);

    /* Nenhuma turma reconheceu os estudantes marcados (alocação por convidado,
       estudante que trocou de turma): em vez de não abrir nada, manda a seleção
       para todas as turmas e deixa a tela de alocação resolver. */
    if (!targets.length) {
      return rooms.map(room => ({ ...room, userIds: selectedUserIds }));
    }

    return targets;
  };

  /* Uma aba por turma. Vai pela URL, e não por state, porque aba nova é outro
     documento — é o mesmo caminho que o "Disponibilizar" das atividades
     sugeridas do relatório da avaliação diagnóstica usa. O type=diagnostic diz
     de onde o link veio: é o que libera, lá, Regional e Escola em "Todas". */
  const openAllocate = () => {
    /* Faltando qualquer um dos dois lados da escolha, o clique só explica o
       que falta: o botão parece desabilitado mas continua clicável, porque um
       <button disabled> engole o clique e não sobraria como avisar. */
    if (!selectedActivityId || !selectedUserIds.length) {
      toastr.warning(
        "Atenção",
        "Selecione os estudantes na tabela acima e uma atividade sugerida para disponibilizar."
      );
      return;
    }

    const targets = allocationTargets();

    targets.forEach(target => {
      const params = new URLSearchParams();

      params.set("type", "diagnostic");

      if (target.groupId) params.set("group_id", target.groupId);
      if (target.regionId) params.set("region_id", target.regionId);
      if (target.schoolId) params.set("school_id", target.schoolId);
      if (target.roomId) params.set("room_id", target.roomId);
      if (target.userIds.length) {
        params.set("user_ids", target.userIds.join(","));
      }

      window
        .open(
          `/activity/allocate/${selectedActivityId}?${params.toString()}`,
          "_blank"
        )
        ?.focus();
    });

    if (targets.length > 1) {
      toastr.info(
        "Disponibilizar",
        `Abrimos ${targets.length} abas de alocação, uma por turma.`
      );
    }
  };

  const abilityCodes = abilities
    .map(ability => ability.code)
    .filter(Boolean)
    .join(", ");

  const missingSelection = !selectedActivityId || !selectedUserIds.length;

  const studentsLabel = !selectedUserIds.length
    ? "nenhum estudante"
    : wholeClass
    ? "a turma toda"
    : `${selectedUserIds.length} estudante(s) selecionado(s)`;

  const activityLabel = selectedActivityId
    ? "1 atividade"
    : "Nenhuma atividade";

  /* Nenhuma habilidade trouxe sugestão: não há o que escolher, então o resumo
     ao lado do botão só repetiria isso ("Nenhuma atividade para nenhum
     estudante") sem o professor ter como mudar. */
  const hasSuggestions = abilityGroups.some(group => group.activities.length);

  const renderActivity = activity => {
    const selected = selectedActivityId === activity.id;
    /* O fundo vai nas DUAS metades do card. Pintar só o ContentCard.Main
       deixava a coluna da direita branca, e o selecionado aparecia como meio
       card colorido. */
    const background = selected ? "#e8f1ff" : "#FFFFFF";

    return (
      /* Cards da mesma linha com a mesma altura: a coluna vira flex e o card é
         o item que estica. Sem isso cada card fica com a altura do próprio
         texto, e um título de duas linhas deixa o vizinho mais baixo. */
      <Col xs="12" md="6" lg="4" className="d-flex" key={activity.id}>
        {/* O card inteiro seleciona: mirar a bolinha de 16px para escolher uma
            atividade é preciso demais para o que o clique faz. O "Visualizar" e
            a própria bolinha param a propagação, senão o clique deles contaria
            duas vezes. */}
        <div
          onClick={() => toggleActivity(activity.id)}
          className="d-flex w-100"
          style={{ cursor: "pointer" }}
        >
          <ContentCard.Root height="auto">
            <ContentCard.Main bgColor={background}>
              <ContentCard.Header title={activity.name} />
              <ContentCard.Body>
                {/* Cada informação no seu <div>: o ContentCard.Item repassa os
                    filhos sem envolvê-los, e os dois textos soltos viravam uma
                    linha só ("1º Ano EFDisciplina: ..."). */}
                <ContentCard.Item>
                  <div>Série: {activity.serie_name ?? "-"}</div>
                </ContentCard.Item>
                <ContentCard.Item>
                  <div>Disciplina: {activity.discipline_name ?? "-"}</div>
                </ContentCard.Item>
              </ContentCard.Body>
            </ContentCard.Main>
            {/* Coluna da direita própria, e não o ContentCard.Footer: ele é
                w-100 e disputava a largura com o conteúdo do card. Aqui ela
                fica nos 25% e o texto usa o resto. */}
            <div
              className="d-flex flex-column align-items-center justify-content-center p-3"
              style={{
                width: "25%",
                minWidth: "25%",
                backgroundColor: background
              }}
            >
              {/* Radio, e não checkbox: a escolha é de uma atividade só, e o
                  `name` compartilhado atravessa os grupos de habilidade —
                  marcar em um desmarca no outro. */}
              <input
                type="radio"
                name="atividade-sugerida"
                className="mb-2"
                style={{ width: "16px", height: "16px" }}
                checked={selected}
                onClick={event => event.stopPropagation()}
                onChange={() => setSelectedActivityId(activity.id)}
              />
              <Button
                size="sm"
                color="light"
                onClick={event => {
                  /* O card é um <a>: sem o preventDefault o clique também
                     navegaria. */
                  event.preventDefault();
                  event.stopPropagation();
                  setViewedActivity(activity);
                }}
              >
                Visualizar
              </Button>
            </div>
          </ContentCard.Root>
        </div>
      </Col>
    );
  };

  return (
    /* A seção é um bloco à parte do resumo: borda e fundo próprios para ela não
       se ler como continuação da tabela de estudantes acima. */
    <div className="w-100 mt-4 p-3 border rounded bg-white">
      <ActivityViewModal
        activity={viewedActivity}
        onClose={() => setViewedActivity(null)}
      />
      <div className="d-flex align-items-center mb-3">
        <h4 className="mb-0 mr-2">Atividades sugeridas</h4>
        {/* A instrução de uso vive no tooltip: ela é longa e só interessa na
            primeira vez, então ocupar duas linhas fixas do cabeçalho custaria
            mais do que ajuda. */}
        <CTooltip content="Atividades sugeridas para cada habilidade desta atividade. Selecione os estudantes na tabela acima, uma atividade e clique em Disponibilizar.">
          <i
            className="fa fa-question-circle text-muted"
            style={{ fontSize: "1rem", cursor: "help" }}
          ></i>
        </CTooltip>
      </div>
      {abilityCodes && (
        <p className="text-muted small mb-3">
          Habilidades desta atividade: {abilityCodes}
        </p>
      )}
      {loading ? (
        <Loading />
      ) : !abilities.length ? (
        <div className="text-muted">
          Esta atividade não possui habilidades cadastradas, então não há
          atividades sugeridas.
        </div>
      ) : (
        <>
          {/* A lista de sugestões não tem tamanho previsível — são vários
              grupos, um por habilidade: sem a altura máxima ela empurraria o
              "Disponibilizar" para fora da modal. */}
          <div style={{ maxHeight: "460px", overflowY: "auto" }}>
            {abilityGroups.map(group => (
              <div className="mb-3" key={group.abilityId}>
                <p className="mb-0">
                  {group.activities.length} atividade(s) encontrada(s) para a
                  Habilidade:
                  <br />
                  {group.code ?? "(Sem código)"}
                  {group.description ? ` - ${group.description}` : ""}
                </p>
                {group.activities.length ? (
                  /* mx-0 na Row: as margens negativas de -15px dela estouram a
                     largura do container que rola, e o que aparecia era uma
                     barra horizontal. Com elas zeradas as colunas quebram em
                     linhas de três, que é o que o lg das colunas já diz. */
                  <Row className="mx-0">
                    {group.activities.map(renderActivity)}
                  </Row>
                ) : (
                  <small className="text-muted">
                    Nenhuma atividade encontrada para esta habilidade.
                  </small>
                )}
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-end mt-3">
            {hasSuggestions && (
              <small className="text-muted mr-3">
                {activityLabel} para {studentsLabel}
              </small>
            )}
            {/* A classe `disabled` dá a aparência de desabilitado sem o
                atributo: o clique ainda chega no openAllocate, que é quem diz
                o que falta selecionar. */}
            <Button
              color="primary"
              className={missingSelection ? "disabled" : undefined}
              style={missingSelection ? { cursor: "not-allowed" } : undefined}
              aria-disabled={missingSelection}
              onClick={openAllocate}
            >
              Disponibilizar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
